/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Component, Inject, OnInit } from '@angular/core';
import { DxListTypes } from 'devextreme-angular/ui/list';
import { SingleMultipleAllOrNone } from 'devextreme/common';
import {
  IProjectDataFacade,
  PROJECT_DATA_FACADE_TOKEN,
} from '../../../../../data-access/src/lib/domain/facades-models/project-data-facade-interface';
import { Observable, Subscription } from 'rxjs';
import { ParamCategory } from '../../models/add-project-parameter-category';
import { ProjectParameterDataItems } from '@hcworkspace/sysque/toolbelt/feature';
import { ProjectDataParamInfo } from '../../models/add-project-data-params-info';
import { GlobalConstants } from '../../constants/global-constants';
import { ProjectParameterInfo } from '../../models/add-project-parameter-info';
import { ProjectDataReturnCodes } from '../../models/project-data-return-codes';
import { AlertStatus, IAlert } from "../../../../../../shared/ui/src/lib/components/alert/alert-types";

@Component({
  selector: 'sysque-add-project-parameter',
  templateUrl: './add-project-parameter.component.html',
  styleUrls: ['./add-project-parameter.component.scss'],
  standalone: false
})
export class AddProjectParameterComponent implements OnInit {
  public categories: string[] = [];
  public disciplines: string[] = [];
  public parameterCategories: string[] = [];
  public parameterGroups: string[] = [];
  public groupings: string[] = [];
  public selectAllModeValue: DxListTypes.SelectAllMode = 'allPages';
  public selectionModeValue: SingleMultipleAllOrNone = 'all';
  public selectByClick = false;
  public alertStatus = AlertStatus.Warning;
  public warningMessage = '';
  public paramCategories$: Observable<ParamCategory[]>;
  public paramCategoriesSubscription: Subscription;
  public addProjectParametersFromJson$: Observable<ProjectParameterDataItems[]>;
  public projectParametersFromJsonSubscription: Subscription;
  public definitionGroupParameters$: Observable<ProjectDataParamInfo[]>;
  public definitionGroupParametersSubscription: Subscription;
  public builtInGroupParameters$: Observable<ProjectDataParamInfo[]>;
  public builtInGroupParametersSubscription: Subscription;
  public parameterData: ProjectParameterDataItems[];
  public selectedParameterCategory: string = '';
  public selectedParameterGroup: string = '';
  public selectedParameterGrouping: string = '';
  public selectedParameterCategories: string[] = [];
  public isProjectParameter: boolean = true;
  public projectParameterInfo: ProjectParameterInfo;
  public get alert(): IAlert {
    return {
      message: this.warningMessage,
      type: this.alertStatus,
      isDismissable: true,
    };
  }

  constructor(@Inject(PROJECT_DATA_FACADE_TOKEN) private facade: IProjectDataFacade) {}

  /// Public Methods
  /**
   * Initializes the component.
   * Retrieves parameter categories, disciplines, parameter groups, and groupings.
   * Sets the parameter categories to the common parameter categories.
   */
  public ngOnInit(): void {
    window.addEventListener('storage', this.storageEventListener.bind(this));
    this.getParamCategories();
    this.getDiscipline();
    this.getParameterGroups();
    this.getGroupings();
    this.projectParameterInfo = new ProjectParameterInfo();
    this.parameterCategories = this.getParameterCategories(GlobalConstants.Common);
    this.setDefaultProjectParameterValues();
  }

  /**
   * Lifecycle hook that is called when the component is destroyed.
   * It unsubscribes from all the subscriptions to prevent memory leaks.
   */
  public ngOnDestroy(): void {
    this.paramCategoriesSubscription.unsubscribe();
    this.projectParametersFromJsonSubscription.unsubscribe();
    this.definitionGroupParametersSubscription.unsubscribe();
    this.builtInGroupParametersSubscription.unsubscribe();
  }

  /**
   * Retrieves the parameter categories based on the provided discipline.
   * @param discipline - The discipline for which to retrieve the parameter categories.
   * @returns An array of parameter categories.
   */
  public getParameterCategories(discipline: string): string[] {
    switch (discipline) {
      case GlobalConstants.Common:
        return this.parameterData[0].projectParameterDataItems.common;
      case GlobalConstants.Structural:
        return this.parameterData[0].projectParameterDataItems.structural;
      case GlobalConstants.HVAC:
        return this.parameterData[0].projectParameterDataItems.hvac;
      case GlobalConstants.Energy:
        return this.parameterData[0].projectParameterDataItems.energy;
      case GlobalConstants.Electrical:
        return this.parameterData[0].projectParameterDataItems.electrical;
      case GlobalConstants.Piping:
        return this.parameterData[0].projectParameterDataItems.piping;
      case GlobalConstants.Infrastructure:
        return this.parameterData[0].projectParameterDataItems.infrastructure;
    }
  }

  /**
   * Handles the initialization event for the component.
   * @param e - The event object.
   */
  public onInitialized(e: any): void {
    e.component.option(GlobalConstants.SelectAllText, GlobalConstants.Categories);
  }

  /**
   * Handles the change event when the parameter name value is updated.
   *
   * @param name - The new value of the parameter name.
   */
  public onParameterNameValueChange(name: string): void {
    this.projectParameterInfo.projectParameterName = name;
  }
  /**
   * Handles the change event when the discipline value is changed.
   * Updates the parameter categories based on the selected discipline value.
   *
   * @param e - The event object containing the new discipline value.
   */
  public onDisciplineValueChanged(e: any): void {
    this.parameterCategories = this.getParameterCategories(e.value);
  }

  /**
   * Handles the change event when the parameter category value is changed.
   * @param e - The event object containing the new value.
   */
  public onParameterCategoryValueChanged(e: any): void {
    this.projectParameterInfo.parameterCategory = e.value;
  }

  /**
   * Handles the value change event of the parameter group.
   * @param e - The event object containing the new value.
   */
  public onParameterGroupValueChanged(e: any): void {
    this.projectParameterInfo.parameterGroup = e.value;
  }

  /**
   * Handles the change event when the grouping value is changed.
   * @param e - The event object containing the new value.
   */
  public onGroupingValueChanged(e: any): void {
    this.projectParameterInfo.grouping = e.value;
  }

  /**
   * Handles the selection change event of the categories list.
   * @param e - The event object containing the added and removed items.
   */
  public onCategoriesListSelectionChanged(e: any): void {
    e.addedItems.length > 0 ? this.updateAddedItems(e.addedItems) : this.updateRemovedItems(e.removedItems);
  }

  /**
   * Handles the change event when the parameter type value is changed.
   * @param e - The event object containing the new value.
   */
  public onParameterTypeValueChanged(e: any): void {
    this.isProjectParameter = e.value === GlobalConstants.Shared;
    this.onDismissClick();
  }

  /**
   * Handles the click event when the dismiss button is clicked.
   */
  public onDismissClick(): void {
    this.isProjectParameter ? this.facade.changeDialogDimensions(645) : this.facade.changeDialogDimensions(620);
    this.warningMessage = '';
  }

  /**
   * Saves the data by adding the project parameter using the facade.
   * If the data is not valid, the method returns early without saving.
   */
  public saveData(): void {
    if (!this.validateData()) {
      return;
    }
    this.projectParameterInfo.isShared = this.isProjectParameter;
    this.facade.addProjectParameter(this.projectParameterInfo);
  }

  /// end of Public methods

  /// Private Methods

  /**
   * Sets the default values for the new project parameter.
   */
  private setDefaultProjectParameterValues(): void {
    this.projectParameterInfo.isShared = true;
    this.projectParameterInfo.grouping = GlobalConstants.IdentityData;
    this.projectParameterInfo.parameterGroup = GlobalConstants.IdentityData;
    this.projectParameterInfo.parameterCategory = this.parameterCategories[0];
    this.projectParameterInfo.categorySet = this.selectedParameterCategories[0];
  }

  /**
   * Updates the added items in the new project parameter information.
   * @param addedItems - An array of items to be added.
   */
  private updateAddedItems(addedParameterCategories: string[]): void {
    if (addedParameterCategories.length > 1) {
      this.projectParameterInfo.categorySet = '';
      addedParameterCategories.forEach(item => {
        if (this.projectParameterInfo.categorySet === '') {
          this.projectParameterInfo.categorySet = item;
        } else {
          this.projectParameterInfo.categorySet += ',' + item;
        }
      });
    } else if (this.projectParameterInfo.categorySet === '') {
      this.projectParameterInfo.categorySet += addedParameterCategories[0];
    } else {
      this.projectParameterInfo.categorySet += ',' + addedParameterCategories[0];
    }
  }

  /**
   * Updates the removed items in the new project parameter information.
   * @param removedItems - An array of items to be removed.
   */
  private updateRemovedItems(removedParameterCategories: string[]): void {
    if (removedParameterCategories.length > 1) {
      removedParameterCategories.forEach(item => {
        this.projectParameterInfo.categorySet = this.projectParameterInfo.categorySet
          .split(',')
          .filter(x => x !== item)
          .join(',');
      });
    } else {
      this.projectParameterInfo.categorySet = this.projectParameterInfo.categorySet
        .split(',')
        .filter(x => x !== removedParameterCategories[0])
        .join(',');
    }
  }

  /**
   * Validates the data for adding a project parameter.
   * @returns {boolean} - Returns true if the data is valid, otherwise false.
   */
  private validateData(): boolean {
    if (
      this.projectParameterInfo.projectParameterName === undefined ||
      this.projectParameterInfo.projectParameterName === ''
    ) {
      this.warningMessage = GlobalConstants.ADD_PARAMETER_NAME_ERROR;
      return this.getWarningInfo(AlertStatus.Warning);
    }
    if (this.projectParameterInfo.categorySet === undefined || this.projectParameterInfo.categorySet === '') {
      this.warningMessage = GlobalConstants.ADD_PARAMETER_CATEGORIES_ERROR;
      return this.getWarningInfo(AlertStatus.Warning);
    }
    return true;
  }

  /**
   * Handles the storage event listener.
   *
   * @param event - The storage event object.
   */
  public storageEventListener(event: StorageEvent): void {
    if (
      event &&
      event.key === GlobalConstants.ADD_PROJECT_PARAMETER_RESPONSE &&
      event.newValue !== event.oldValue &&
      event.newValue !== null &&
      JSON.parse(event.newValue).response >= 0
    ) {
      const data = JSON.parse(event.newValue);
      switch (data.response) {
        case ProjectDataReturnCodes.ProjectParamCreated:
          this.getWarningInfo(AlertStatus.Success);
          this.warningMessage = GlobalConstants.ADD_PROJECT_PARAMETER_SUCCESSFULLY;

          break;
        case ProjectDataReturnCodes.ProjectParamAlreadyExists:
          this.getWarningInfo(AlertStatus.Warning);
          this.warningMessage = GlobalConstants.ADD_PROJECT_PARAMETER_ERROR;

          break;
        case ProjectDataReturnCodes.SharedParamCreated:
          this.getWarningInfo(AlertStatus.Success);
          this.warningMessage = GlobalConstants.ADD_SHARED_PARAMETER_SUCCESSFULLY;

          break;
        case ProjectDataReturnCodes.SharedParamAlreadyExists:
          this.warningMessage = GlobalConstants.ADD_SHARED_PARAMETER_ERROR;
          this.getWarningInfo(AlertStatus.Warning);
          break;
      }
      this.facade.updateLocalStorage(GlobalConstants.ADD_PROJECT_PARAMETER_RESPONSE, {
        response: -2,
        paramList: data.paramList,
      });
    }
  }

  /**
   * Gets the warning information.
   * Sets the alert type to Warning and changes the dialog dimensions.
   */
  private getWarningInfo(alertStatus: AlertStatus): boolean {
    this.alertStatus = alertStatus;
    this.isProjectParameter ? this.facade.changeDialogDimensions(740) : this.facade.changeDialogDimensions(715);

    if (alertStatus === AlertStatus.Success) {
      const element = document.getElementById('parameterNameText') as HTMLInputElement;
      element.value = '';
      this.projectParameterInfo.projectParameterName = '';
    }

    return false;
  }

  /**
   * Retrieves the built-in group parameters and sets the groupings array.
   */
  private getGroupings(): void {
    this.builtInGroupParameters$ = this.facade.getBuiltInGroupParameters();
    this.builtInGroupParametersSubscription = this.builtInGroupParameters$.subscribe((data) => {
      this.groupings = data.map(item => item.paramValue).sort();
      this.selectedParameterGrouping =
        this.groupings.findIndex(x => x == GlobalConstants.IdentityData) >= 0 ? GlobalConstants.IdentityData : '-1';
    });
  }

  /**
   * Retrieves the parameter groups from the facade and assigns them to the component's properties.
   */
  private getParameterGroups(): void {
    this.definitionGroupParameters$ = this.facade.getDefinitionGroupParameters();
    this.definitionGroupParametersSubscription = this.definitionGroupParameters$.subscribe((data) => {
      this.parameterGroups = data.map(item => item.paramValue).sort();
      this.selectedParameterGroup =
        this.parameterGroups.findIndex(x => x == GlobalConstants.IdentityData) >= 0
          ? GlobalConstants.IdentityData
          : '-1';
    });
  }

  /**
   * Retrieves the discipline data from the facade and assigns it to the component's `disciplines` property.
   */
  private getDiscipline(): void {
    this.addProjectParametersFromJson$ = this.facade.initializeAddProjectParametersFromJson();
    this.projectParametersFromJsonSubscription = this.addProjectParametersFromJson$.subscribe((data) => {
      this.parameterData = data;
      this.parameterData.forEach(item => {
        this.disciplines = item.projectParameterDataItems.discipline;
      });
    });
  }

  /**
   * Retrieves the parameter categories based on the selected category.
   * Sets the selected category and subscribes to the paramCategories$ observable to update the categories.
   */
  private getParamCategories(): void {
    const currentParameterCategory = this.facade.getStorage(GlobalConstants.PROJECT_DATA_CURRENT_CATEGORY) as string;
    this.selectedParameterCategories.push(currentParameterCategory);
    this.paramCategories$ = this.facade.getParamCategories(currentParameterCategory);
    this.paramCategoriesSubscription = this.paramCategories$.subscribe((data) => {
      this.categories = data.map(item => item.name);
    });
  }

  /// end of Private methods
}
